import React, { useState, useEffect, useRef, useCallback, useReducer, lazy, Suspense } from 'react';
import pb, { getImageUrl } from '../services/pocketbase';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import '../styles/Home.css';

const getPetLabel = (type) => {
  const labels = { dog: 'Собака', cat: 'Кошка', rodent: 'Грызун', bird: 'Птица', other: 'Животное' };
  return labels[type] || 'Животное';
};

const PostsMap = lazy(() => import('../components/PostsMap'));

function Home() {
  // Объединяем posts/page/hasMore в один reducer — сброс одной операцией
  const [feedState, dispatchFeed] = useReducer((state, action) => {
    switch (action.type) {
      case 'RESET':   return { posts: [], page: 1, hasMore: true };
      case 'SET':     return { ...state, posts: action.posts, hasMore: action.hasMore };
      case 'APPEND':  return { ...state, posts: [...state.posts, ...action.posts], hasMore: action.hasMore };
      case 'NEXT_PAGE': return { ...state, page: state.page + 1 };
      default: return state;
    }
  }, { posts: [], page: 1, hasMore: true });
  const { posts, page, hasMore } = feedState;
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState('all');
  const loaderRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimerRef = useRef(null);
  const [userCoords, setUserCoords] = useState(null);
  const [userCity, setUserCity] = useState(() => localStorage.getItem('awoo_city') || '');
  const [cityFilter, setCityFilter] = useState('all');
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [cityInput, setCityInput] = useState('');
  const [cityCoords, setCityCoords] = useState(() => {
    const saved = localStorage.getItem('awoo_city_coords');
    return saved ? JSON.parse(saved) : null;
  });
  const saveCityCoords = (coords) => {
    setCityCoords(coords);
    localStorage.setItem('awoo_city_coords', JSON.stringify(coords));
  };
  const [viewMode, setViewMode] = useState('list');
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapCenterCoords, setMapCenterCoords] = useState(null);
  const mapSearchTimerRef = useRef(null);
  const mapSearchAbortRef = useRef(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [badges, setBadges] = useState({ ls: 0, cs: 0 });
  const [filterBadges, setFilterBadges] = useState({ lost: 0, found: 0, help: 0, services: 0 });
  const navigate = useNavigate();
  const currentUser = pb.authStore.model;
  const currentUserRole = currentUser?.userRole;
  const isVolunteer = currentUserRole === 'volunteer' || currentUserRole === 'volunteer_pending';

  const handleNotifClick = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    try {
      const { default: OneSignal } = await import('react-onesignal');
      const permission = OneSignal.Notifications.permission;
      if (!permission) {
        await OneSignal.Notifications.requestPermission();
      }
    } catch (e) {}
    navigate('/profile', { state: { initialTab: 'notifications' } });
  };

  // При старте — если cityCoords нет, геокодируем сохранённый город
  useEffect(() => {
    const savedCity = localStorage.getItem('awoo_city');
    const savedCoords = localStorage.getItem('awoo_city_coords');
    if (savedCity && !savedCoords) {
      fetch(`https://nominatim.openstreetmap.org/search?city=${encodeURIComponent(savedCity)}&format=json&limit=1&accept-language=ru`)
        .then(r => r.json())
        .then(data => {
          if (data[0]) saveCityCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
        })
        .catch(() => {});
    }
    const profileCity = pb.authStore.model?.city;
    if (profileCity && !savedCity) {
      setUserCity(profileCity);
      setCityFilter('city');
      localStorage.setItem('awoo_city', profileCity);
      fetch(`https://nominatim.openstreetmap.org/search?city=${encodeURIComponent(profileCity)}&format=json&limit=1&accept-language=ru`)
        .then(r => r.json())
        .then(data => {
          if (data[0]) saveCityCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (viewMode !== 'map' && filter !== 'map') return;
    import('../components/PostsMap');
  }, [viewMode, filter]);

  useEffect(() => {
    return () => {
      if (mapSearchAbortRef.current) {
        mapSearchAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const dismissed = localStorage.getItem('awoo_install_banner_dismissed') === '1';
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (!dismissed && !isStandalone) {
      setShowInstallBanner(true);
    }
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          setUserCoords({ lat: latitude, lng: longitude });
          try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=ru`);
            const data = await res.json();
            const addr = data.address;
            const rawCity = addr.city || addr.town || addr.village || addr.county || '';
            // Убираем суффиксы Nominatim: "городской округ Красноярск" → "Красноярск"
            const detectedCity = rawCity.replace(/^(городской округ|муниципальный округ|район)\s+/i, '').trim();
            if (detectedCity) {
              setUserCity(detectedCity);
              setCityFilter('city');
              saveCityCoords({ lat: latitude, lng: longitude });
              localStorage.setItem('awoo_city', detectedCity);
            }
          } catch {}
        },
        () => {}
      );
    }
  }, []);

  // Fetch для режима карты — грузит посты с координатами (лимит 300)
  const doFetchMap = useCallback(async (query, currentCityFilter, currentCity) => {
    setLoading(true);
    try {
      const parts = ['lat != "" && lng != ""'];
      const q = (query || '').trim();
      if (q) {
        const escaped = q.replace(/"/g, '\\"');
        parts.push(`(petName ?~ "${escaped}" || breed ?~ "${escaped}" || description ?~ "${escaped}" || location ?~ "${escaped}")`);
      }
      if (currentCityFilter === 'city' && currentCity) {
        parts.push(`city ?~ "${currentCity.replace(/"/g, '\\"')}"`);
      }
      // Используем getList с лимитом вместо getFullList — избегаем загрузки всех страниц
      const records = await pb.collection('posts').getList(1, 300, {
        filter: parts.join(' && '),
        sort: '-created',
      });
      dispatchFeed({ type: 'SET', posts: records.items, hasMore: false });
    } catch (error) {
      console.error('Error fetching map posts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Единый fetch — принимает все параметры явно, нет замыканий
  const doFetch = useCallback(async (pageNum, reset, query, currentFilter, currentCityFilter, currentCity) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const parts = [];
      if (currentFilter === 'lost') parts.push('type = "lost"');
      else if (currentFilter === 'found') parts.push('type = "found"');
      else if (currentFilter === 'help') parts.push('type = "help"');
      else if (currentFilter === 'services') parts.push('type = "service"');
      else if (currentFilter === 'dog') parts.push('petType = "dog"');
      else if (currentFilter === 'cat') parts.push('petType = "cat"');
      else if (currentFilter === 'other') parts.push('petType != "dog" && petType != "cat"');
      if (currentCityFilter === 'city' && currentCity) {
        parts.push(`city ?~ "${currentCity.replace(/"/g, '\\"')}"`);
      }

      const q = (query || '').trim();
      if (q) {
        const escaped = q.replace(/"/g, '\\"');
        parts.push(`(petName ?~ "${escaped}" || breed ?~ "${escaped}" || description ?~ "${escaped}" || helpDescription ?~ "${escaped}" || location ?~ "${escaped}" || userName ?~ "${escaped}")`);
      }

      const queryOptions = { sort: '-created' };
      if (parts.length) queryOptions.filter = parts.join(' && ');

      const PER_PAGE = 20;
      const records = await pb.collection('posts').getList(pageNum, PER_PAGE, queryOptions);
      if (reset) dispatchFeed({ type: 'SET', posts: records.items, hasMore: records.items.length === PER_PAGE });
      else dispatchFeed({ type: 'APPEND', posts: records.items, hasMore: records.items.length === PER_PAGE });
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Сброс и перезагрузка при смене фильтра или поиска (с debounce для поиска)
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    const delay = searchQuery !== '' ? 400 : 0;
    searchTimerRef.current = setTimeout(() => {
      dispatchFeed({ type: 'RESET' });
      if (filter === 'map') {
        doFetchMap(searchQuery, cityFilter, userCity);
      } else {
        doFetch(1, true, searchQuery, filter, cityFilter, userCity);
      }
    }, delay);
    return () => clearTimeout(searchTimerRef.current);
  }, [filter, searchQuery, cityFilter, doFetch, doFetchMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Подгрузка следующей страницы
  useEffect(() => {
    if (page === 1) return;
    if (filter === "map") return;
    doFetch(page, false, searchQuery, filter, cityFilter, userCity);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          dispatchFeed({ type: 'NEXT_PAGE' });
        }
      },
      { threshold: 0.5 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 15000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBadges = async () => {
    if (!currentUser?.id) return;
    const lastVisitLS = localStorage.getItem('lastVisitLS') || '2000-01-01';
    const lastVisitCS = localStorage.getItem('lastVisitCS') || '2000-01-01';
    try {
      const msgs = await pb.collection('messages').getList(1, 200, {
        filter: `userId != "${currentUser.id}" && chatType = "private"`,
        fields: 'id,chatId,read'
      });
      const unreadMsgs = msgs.items.filter(m => m.chatId && m.chatId.includes(currentUser.id) && !m.read).length;

      let csCount = 0;
      try {
        const cs = await pb.collection('blacklist').getList(1, 1, {
          filter: `created > "${lastVisitCS}"`, fields: 'id'
        });
        csCount = cs.totalItems;
      } catch (e) {}

      const toFilter = (iso) => iso.replace('T', ' ').replace('Z', '').slice(0, 19);
      const lastLost     = toFilter(localStorage.getItem('lastVisitFilterLost')     || '2000-01-01T00:00:00Z');
      const lastFound    = toFilter(localStorage.getItem('lastVisitFilterFound')    || '2000-01-01T00:00:00Z');
      const lastHelp     = toFilter(localStorage.getItem('lastVisitFilterHelp')     || '2000-01-01T00:00:00Z');
      const lastServices = toFilter(localStorage.getItem('lastVisitFilterServices') || '2000-01-01T00:00:00Z');
      const [lostRes, foundRes, helpRes, servicesRes] = await Promise.all([
        pb.collection('posts').getList(1, 1, { filter: `type = "lost" && created > "${lastLost}" && userId != "${currentUser.id}"`, fields: 'id' }),
        pb.collection('posts').getList(1, 1, { filter: `type = "found" && created > "${lastFound}" && userId != "${currentUser.id}"`, fields: 'id' }),
        pb.collection('posts').getList(1, 1, { filter: `type = "help" && created > "${lastHelp}" && userId != "${currentUser.id}"`, fields: 'id' }),
        pb.collection('posts').getList(1, 1, { filter: `type = "service" && created > "${lastServices}" && userId != "${currentUser.id}"`, fields: 'id' }),
      ]);
      setFilterBadges({ lost: lostRes.totalItems, found: foundRes.totalItems, help: helpRes.totalItems, services: servicesRes.totalItems });
      setBadges({ ls: unreadMsgs, cs: csCount });
    } catch (e) {}
  };

  const filters = [
    { value: 'lost',     label: '🔍 Потерялись', badge: filterBadges.lost },
    { value: 'found',    label: '🐾 Найдены',    badge: filterBadges.found },
    { value: 'help',     label: '🆘 Помощь',     badge: filterBadges.help },
    { value: 'services', label: '🛎️ Услуги',     badge: filterBadges.services },
    { value: 'map',      label: '🗺️ На карте' },
    { value: 'all',      label: 'Все' },
    { value: 'dog',      label: '🐕 Собаки' },
    { value: 'cat',      label: '🐈 Кошки' },
    { value: 'other',    label: '🐦 Другое' },
  ];

  const getUrgencyBadge = (urgency) => {
    if (urgency === 'critical') return { label: '🔴 Критично', color: '#F44336' };
    if (urgency === 'urgent')   return { label: '🟠 Срочно',   color: '#FF9800' };
    return null;
  };

  return (
    <div className="home-page">
      <div className="header">
        <div className="header-top">
          <div>
            <div className="logo" style={{display:'flex', alignItems:'center', gap:'10px'}}>
              AWOO
              <svg width="28" height="28" viewBox="0 0 512 512" fill="white" style={{opacity:0.95}}>
                <path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5s.3-86.2 32.6-96.8s70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7 .9 78.5 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7c-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2C84.9 480 64 459.1 64 433.3v-1.6c0-10.4 1.6-20.8 5.2-30.5zM421.8 282.7c-24.5-14-29.1-51.7-10.2-84.1s54-47.3 78.5-33.3s29.1 51.7 10.2 84.1s-54 47.3-78.5 33.3zM310.1 189.7c-32.3-10.6-46.9-53.9-32.6-96.8s52.1-69.1 84.4-58.5s46.9 53.9 32.6 96.8s-52.1 69.1-84.4 58.5z"/>
              </svg>
            </div>
            <div className="logo-subtitle">Розыск, помощь и уход за животными.</div>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
            <div className="notification-icon" onClick={handleNotifClick} style={{cursor:'pointer'}}>🔔</div>
            <button onClick={() => { localStorage.setItem('lastVisitLS', new Date().toISOString()); navigate('/profile'); }} style={{background:'rgba(255,255,255,0.2)', border:'none', color:'white', borderRadius:'8px', padding:'6px 10px', cursor:'pointer', fontSize:'13px', fontWeight:'700', position:'relative'}}>
              ЛС
              {badges.ls > 0 && <span style={{position:'absolute', top:'-6px', right:'-6px', background:'#FF6B35', color:'white', fontSize:'10px', fontWeight:'700', minWidth:'16px', height:'16px', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px', border:'2px solid rgba(59,89,152,0.95)'}}>{badges.ls > 99 ? '99+' : badges.ls}</span>}
            </button>
            {isVolunteer && (
              <button onClick={() => { localStorage.setItem('lastVisitCS', new Date().toISOString()); navigate('/blacklist'); }} style={{background:'rgba(220,50,50,0.5)', border:'none', color:'white', borderRadius:'8px', padding:'6px 10px', cursor:'pointer', fontSize:'13px', fontWeight:'700', position:'relative'}}>
                ЧС
                {badges.cs > 0 && <span style={{position:'absolute', top:'-6px', right:'-6px', background:'#FF6B35', color:'white', fontSize:'10px', fontWeight:'700', minWidth:'16px', height:'16px', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px', border:'2px solid rgba(59,89,152,0.95)'}}>{badges.cs > 99 ? '99+' : badges.cs}</span>}
              </button>
            )}
          </div>
        </div>
        {/* Переключатель города */}
        <div style={{ display: 'flex', gap: '6px', padding: '6px 16px 8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {userCity && (
            <button
              onClick={() => { setCityFilter('city'); dispatchFeed({ type: 'RESET' }); }}
              style={{
                padding: '5px 12px', borderRadius: '16px', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: '600', transition: 'all 0.2s',
                background: cityFilter === 'city' ? 'white' : 'rgba(255,255,255,0.25)',
                color: cityFilter === 'city' ? '#3B5998' : 'white',
              }}
            >
              📍 {userCity}
            </button>
          )}
          <button
            onClick={() => { setCityFilter('all'); dispatchFeed({ type: 'RESET' }); }}
            style={{
              padding: '5px 12px', borderRadius: '16px', border: 'none', cursor: 'pointer',
              fontSize: '12px', fontWeight: '600', transition: 'all 0.2s',
              background: cityFilter === 'all' ? 'white' : 'rgba(255,255,255,0.25)',
              color: cityFilter === 'all' ? '#3B5998' : 'white',
            }}
          >
            🌍 Все города
          </button>
          {showCityPicker ? (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <input
                autoFocus
                value={cityInput}
                onChange={e => setCityInput(e.target.value)}
                onKeyPress={async e => {
                  if (e.key === 'Enter' && cityInput.trim()) {
                    const cityName = cityInput.trim();
                    setUserCity(cityName);
                    setCityFilter('city');
                    localStorage.setItem('awoo_city', cityName);
                    setCityInput('');
                    setShowCityPicker(false);
                    dispatchFeed({ type: 'RESET' });
                    try {
                      const res = await fetch(`https://nominatim.openstreetmap.org/search?city=${encodeURIComponent(cityName)}&format=json&limit=1&accept-language=ru`);
                      const data = await res.json();
                      if (data[0]) saveCityCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                    } catch {}
                  }
                }}
                placeholder="Введите город..."
                style={{
                  padding: '4px 10px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.5)',
                  background: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '12px',
                  outline: 'none', width: '130px',
                }}
              />
              <button
                onClick={() => setShowCityPicker(false)}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '16px' }}
              >✕</button>
            </div>
          ) : (
            <button
              onClick={() => setShowCityPicker(true)}
              style={{
                padding: '5px 10px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.4)',
                background: 'transparent', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '11px',
              }}
            >
              ✏️ Сменить
            </button>
          )}
        </div>
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Поиск по кличке, породе, приметам..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <span
              onClick={() => setSearchQuery('')}
              style={{cursor:'pointer', fontSize:'16px', color:'rgba(255,255,255,0.7)', padding:'0 4px'}}>
              ✕
            </span>
          )}
        </div>
        <div className="filters">
          {filters.map(f => (
            <button key={f.value} className={`filter-btn ${filter === f.value ? 'active' : ''}`} style={{position:'relative'}} onClick={() => {
              if (f.value === 'lost') localStorage.setItem('lastVisitFilterLost', new Date().toISOString());
              if (f.value === 'found') localStorage.setItem('lastVisitFilterFound', new Date().toISOString());
              if (f.value === 'help') localStorage.setItem('lastVisitFilterHelp', new Date().toISOString());
              if (f.value === 'services') localStorage.setItem('lastVisitFilterServices', new Date().toISOString());
              if (f.value === 'map') setViewMode('map');
              else setViewMode('list');
              setFilter(f.value);
            }}>
              {f.label}
              {f.badge > 0 && <span style={{position:'absolute', top:'-6px', right:'-4px', background:'#FF6B35', color:'white', fontSize:'9px', fontWeight:'700', minWidth:'15px', height:'15px', borderRadius:'8px', display:'inline-flex', alignItems:'center', justifyContent:'center', padding:'0 3px', border:'2px solid white'}}>{f.badge > 99 ? '99+' : f.badge}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="content">
        {showInstallBanner && (
          <div style={{background:'white', margin:'10px 12px 0', borderRadius:'12px', padding:'12px', boxShadow:'0 2px 10px rgba(0,0,0,0.08)', border:'1px solid #EAF1FF'}}>
            <div style={{display:'flex', justifyContent:'space-between', gap:'10px', alignItems:'flex-start'}}>
              <div>
                <div style={{fontSize:'14px', fontWeight:'700', color:'#2F4C8A'}}>📲 Установите AWOO как приложение</div>
                <div style={{fontSize:'12px', color:'#666', marginTop:'4px'}}>Быстрый доступ с экрана телефона и удобнее пользоваться.</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem('awoo_install_banner_dismissed', '1');
                  setShowInstallBanner(false);
                }}
                style={{border:'none', background:'transparent', color:'#999', fontSize:'18px', cursor:'pointer', lineHeight:1}}
                aria-label="Скрыть баннер установки"
              >
                ×
              </button>
            </div>
            <div style={{display:'flex', gap:'8px', marginTop:'10px'}}>
              <button
                type="button"
                onClick={() => setShowInstallModal(true)}
                style={{flex:1, border:'none', borderRadius:'9px', background:'#3B5998', color:'white', fontSize:'13px', fontWeight:'700', padding:'9px 10px', cursor:'pointer'}}
              >
                Инструкция
              </button>
            </div>
          </div>
        )}
        {(viewMode === 'map' || filter === 'map') ? (
          // Используем PostsMap — без дублирования, без лишнего пересоздания
          loading ? (
            <div className="loading-state"><div className="spinner"></div><p>Загрузка...</p></div>
          ) : (
            <div style={{position:'relative'}}>
              <div style={{padding:'8px 12px', background:'white', borderBottom:'1px solid #eee', display:'flex', alignItems:'center', gap:'8px'}}>
                <span>📍</span>
                <input
                  type="text"
                  placeholder="Найти место на карте..."
                  value={mapSearchQuery}
                  onChange={e => {
                    const val = e.target.value;
                    setMapSearchQuery(val);
                    clearTimeout(mapSearchTimerRef.current);
                    if (val.trim().length < 3) return;
                    mapSearchTimerRef.current = setTimeout(async () => {
                      try {
                        if (mapSearchAbortRef.current) {
                          mapSearchAbortRef.current.abort();
                        }
                        const controller = new AbortController();
                        mapSearchAbortRef.current = controller;
                        const res = await fetch(
                          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&limit=1&accept-language=ru`,
                          { signal: controller.signal }
                        );
                        if (controller.signal.aborted) return;
                        const data = await res.json();
                        if (data.length > 0) {
                          setMapCenterCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                        }
                      } catch {}
                    }, 800);
                  }}
                  style={{flex:1, border:'none', outline:'none', fontSize:'14px', color:'#333'}}
                />
                {mapSearchQuery && (
                  <span onClick={() => { setMapSearchQuery(''); setMapCenterCoords(null); }} style={{cursor:'pointer', color:'#999', fontSize:'16px'}}>✕</span>
                )}
              </div>
              <Suspense fallback={<div className="loading-state"><div className="spinner"></div><p>Загрузка карты...</p></div>}>
                <PostsMap
                  posts={posts}
                  userCoords={userCoords}
                  centerCoords={mapCenterCoords || cityCoords}
                  height="calc(100vh - 240px)"
                />
              </Suspense>
            </div>
          )
        ) : loading ? (
          <div className="loading-state"><div className="spinner"></div><p>Загрузка...</p></div>
        ) : posts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🐾</div>
            <h3>Пока нет объявлений</h3>
            <p>Будьте первым, кто создаст объявление!</p>
            <button className="btn-primary" onClick={() => navigate('/create-post')}>Создать объявление</button>
          </div>
        ) : (
          <div className="posts-feed">
            {posts.map(post => {
              const isHelp = post.type === 'help';
              const urgencyBadge = isHelp ? getUrgencyBadge(post.urgency) : null;
              return (
                <div key={post.id} className="post-card" onClick={() => navigate(`/post/${post.id}`)}>
                  <div className="post-header">
                    <div style={{display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap'}}>
                      <span className={`post-type ${post.type}`}>
                        {post.type === 'lost' ? '🔍 Потерялся' : post.type === 'service' ? '🛎️ Услуги' : post.type === 'help' ? '🆘 Нужна помощь' : '🐾 Найден'}
                      </span>
                      {urgencyBadge && (
                        <span style={{fontSize:'11px', fontWeight:'700', color: urgencyBadge.color, background: urgencyBadge.color + '15', borderRadius:'10px', padding:'2px 8px'}}>
                          {urgencyBadge.label}
                        </span>
                      )}
                    </div>
                    <span className="post-time">{getTimeAgo(post.created)}</span>
                  </div>

                  {post.image && (
                    <img src={getImageUrl(post, post.image)} alt={post.petName} className="post-image" />
                  )}

                  {isHelp ? (
                    <>
                      {post.helpDescription && (
                        <div className="post-description" style={{marginTop:'8px', fontWeight:'500', color:'#333'}}>
                          {post.helpDescription.length > 120 ? post.helpDescription.slice(0, 120) + '...' : post.helpDescription}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="post-title">{post.petName || getPetLabel(post.petType)}</div>
                      <div className="post-description">{post.description}</div>
                    </>
                  )}

                  {post.location && (
                    <div className="post-location">📍 {post.location}</div>
                  )}
                  {post.city && post.city !== userCity && (
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>
                      🏙️ {post.city}
                    </div>
                  )}
                  {post.reward && post.type !== 'service' && (
                    <div style={{fontSize:'12px', color:'#E65100', fontWeight:'600', marginTop:'4px'}}>
                      💰 Вознаграждение
                    </div>
                  )}
                  <div style={{display:'flex', alignItems:'center', gap:'12px', marginTop:'10px', paddingTop:'8px', borderTop:'1px solid #F0F0F0'}}>
                    <span style={{fontSize:'12px', color:'#999', display:'flex', alignItems:'center', gap:'3px'}}>
                      👁 {post.views || 0}
                    </span>
                    <span style={{fontSize:'12px', color:'#999', display:'flex', alignItems:'center', gap:'3px'}}>
                      💬 {post.responses || 0}
                    </span>
                    <span style={{fontSize:'12px', color:'#999', display:'flex', alignItems:'center', gap:'3px'}}>
                      ❤️ {post.likes || 0}
                    </span>
                  </div>
                </div>
              );
            })}
            <div ref={loaderRef} style={{height:'40px', display:'flex', alignItems:'center', justifyContent:'center'}}>
              {loadingMore && <div className="spinner" style={{width:'24px', height:'24px'}}></div>}
              {!hasMore && posts.length > 0 && <div style={{fontSize:'13px', color:'#999', padding:'8px'}}>Все объявления загружены</div>}
            </div>
          </div>
        )}
      </div>


      {showInstallModal && (
        <div onClick={() => setShowInstallModal(false)} style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-end'}}>
          <div onClick={e => e.stopPropagation()} style={{background:'white', borderRadius:'24px 24px 0 0', width:'100%', padding:'20px', maxHeight:'80vh', overflowY:'auto'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px'}}>
              <div style={{fontSize:'18px', fontWeight:700}}>📲 Установить приложение</div>
              <button type="button" onClick={() => setShowInstallModal(false)} style={{border:'none', background:'transparent', fontSize:'24px', cursor:'pointer', color:'#999', lineHeight:1}}>×</button>
            </div>

            <div style={{marginBottom:'16px'}}>
              <div style={{fontWeight:700, fontSize:'15px', marginBottom:'8px', color:'#333'}}>Android (Chrome)</div>
              <div style={{fontSize:'14px', color:'#444', lineHeight:1.7}}>
                1) Откройте сайт в браузере Chrome.<br/>
                2) Нажмите меню браузера (⋮) в правом верхнем углу.<br/>
                3) Выберите "Установить приложение" или "Добавить на главный экран".<br/>
                4) Подтвердите установку.
              </div>
            </div>

            <div style={{marginBottom:'16px'}}>
              <div style={{fontWeight:700, fontSize:'15px', marginBottom:'8px', color:'#333'}}>iPhone / iPad (Safari)</div>
              <div style={{fontSize:'14px', color:'#444', lineHeight:1.7}}>
                1) Откройте сайт именно в Safari.<br/>
                2) Нажмите кнопку "Поделиться".<br/>
                3) Выберите "На экран Домой".<br/>
                4) Нажмите "Добавить".
              </div>
              <div style={{marginTop:'10px', fontSize:'12px', color:'#8A6D3B', background:'#FFF8E1', borderRadius:'8px', padding:'8px 10px'}}>
                Важно: в iOS установка из встроенных браузеров (Telegram/Instagram) может не работать.
              </div>
            </div>

            <div>
              <div style={{fontWeight:700, fontSize:'15px', marginBottom:'8px', color:'#333'}}>Если кнопки нет</div>
              <div style={{fontSize:'14px', color:'#444', lineHeight:1.7}}>
                — Обновите страницу и попробуйте снова.<br/>
                — Убедитесь, что используете HTTPS-версию сайта.<br/>
                — Проверьте, не открыт ли сайт во встроенном браузере приложения.
              </div>
            </div>
          </div>
        </div>
      )}
      <BottomNav />
    </div>
  );
}

function getTimeAgo(timestamp) {
  if (!timestamp) return 'недавно';
  const diff = Math.floor((new Date() - new Date(timestamp)) / 1000);
  if (diff < 60) return 'только что';
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  return `${Math.floor(diff / 86400)} дн назад`;
}

export default Home;
